/**
 * useContextualNudge
 *
 * Unified hook for showing contextual nudges with multi-layered gating:
 * 1. Registry config lookup (max shows, cooldown, i18n keys)
 * 2. Server-side frequency tracking (useNudgeDelivery)
 * 3. Concurrent display limit (useNudgeSlot via NudgeGateProvider)
 * 4. Data-driven trigger condition (caller-supplied boolean)
 *
 * Example usage:
 *   const { shouldShow, recordImpression, nudgeProps } = useContextualNudge(
 *     'catalog_add_first_service',  // nudge ID from registry
 *     services.length === 0           // data-driven trigger condition
 *   );
 *
 *   return (
 *     <>
 *       <CatalogPage>...</CatalogPage>
 *       {shouldShow && (
 *         <AppNudge
 *           {...nudgeProps}
 *           open={true}
 *           anchorEl={buttonRef.current}
 *           onClose={() => recordImpression()}
 *         />
 *       )}
 *     </>
 *   );
 */

import { useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { getNudgeConfig } from '@/lib/nudges/registry';
import { useNudgeDelivery } from '@/hooks/useNudgeDelivery';
import { useNudgeSlot } from '@/components/useNudgeSlot';

export interface UseContextualNudgeResult {
  /** Whether this nudge should currently be displayed */
  shouldShow: boolean;
  /** Call this once when the nudge is acknowledged/dismissed by the user */
  recordImpression: () => void;
  /** Props to spread into AppNudge component */
  nudgeProps: {
    title: string;
    variant: 'default' | 'success' | 'warning' | 'info';
    children: ReactNode;
    cta?: { label: string; onClick: () => void };
  };
}

/**
 * Main hook for integrating contextual nudges.
 *
 * @param nudgeId — unique nudge identifier from NUDGE_REGISTRY
 * @param trigger — boolean data-driven condition (e.g., "0 services in catalog")
 *
 * @throws Error if nudgeId is not found in registry or hook is used outside NudgeGateProvider
 */
export function useContextualNudge(nudgeId: string, trigger: boolean): UseContextualNudgeResult {
  const { t } = useTranslation(['admin']);

  // Look up nudge config
  const config = getNudgeConfig(nudgeId);
  if (!config) {
    throw new Error(`Nudge "${nudgeId}" not found in registry. Add it to NUDGE_REGISTRY.`);
  }

  // Server-side persistence gating (max shows + cooldown)
  const { shouldShow: passesServerGating, recordImpression: recordToServer } = useNudgeDelivery(
    nudgeId,
    config.maxShows,
    { cooldownMs: config.cooldownMs }
  );

  // Concurrent display slot gating (max 3 nudges visible simultaneously)
  const hasSlot = useNudgeSlot(nudgeId, trigger && passesServerGating);

  // Final decision: show if trigger is true AND server gate passed AND slot available
  const shouldShow = trigger && passesServerGating && hasSlot;

  // Translate copy from i18n keys — dynamic keys from registry bypass strict i18n typing
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const titleKey = config.titleKey as any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const messageKey = config.messageKey as any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ctaLabelKey = config.ctaLabelKey as any;

  const title = t(titleKey);
  const message = t(messageKey);
  const ctaLabel = config.ctaLabelKey ? t(ctaLabelKey) : 'Got it';

  // When nudge is acknowledged, record impression
  const recordImpression = useCallback(() => {
    recordToServer();
  }, [recordToServer]);

  const nudgeProps = {
    title,
    variant: config.variant || ('default' as const),
    children: message,
    cta: {
      label: ctaLabel,
      onClick: recordImpression,
    },
  };

  return { shouldShow, recordImpression, nudgeProps };
}
